import { Box, Container, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import PositiveButton from 'components/cmn/PositiveButton';
import useInspection from 'hooks/useInspection';
import { IInspectionFinalStandardProps } from 'interface/inspection/InspectionInfo';
import { inspectionLogger, getStepName } from 'utils/inspectionLogger';
import { useAppDispatch } from 'store';
import { postInspectionClickLogs } from 'store/inspection/inspectionSlice';
import { useProject } from 'hooks/useProject';

function ClassificationGuideFinal(): JSX.Element {
  const dispatch = useAppDispatch();
  const { urlProjectId, urlPartCode } = useProject();
  const { inspectionDetail, changeIsClassificationGuideStartFinal, initInspectionFinalBase } = useInspection();
  const { grid } = inspectionDetail || {};

  const handleNext = async () => {
    // 최종 단계 시작 버튼 클릭 로그 (비동기)
    if (urlProjectId && urlPartCode && inspectionDetail?.id) {
      inspectionLogger.logClick({
        projectId: urlProjectId,
        partCode: urlPartCode,
        inspectionId: inspectionDetail.id,
        step: 'final' as const,
        stepName: getStepName('final'),
        actionType: 'next_button',
      });

      // 비동기로 전송 (백그라운드에서 실행)
      inspectionLogger.flush().then((pendingLogs) => {
        if (pendingLogs.length > 0) {
          dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
            .unwrap()
            .then(() => {
              console.log(`✅ 최종 단계 시작 버튼 클릭 로그 ${pendingLogs.length}개 전송 완료`);
            })
            .catch((error) => {
              console.error('❌ 최종 단계 시작 버튼 클릭 로그 전송 실패:', error);
            });
        }
      });
    }

    console.log('시작');
    console.log('inspectionDetail:', inspectionDetail);
    console.log('grid value:', grid);
    
    // grid를 사용해서 방 구조를 만들고, inspectionEndClassification의 항목들을 채우기
    if (grid && typeof grid === 'string' && grid.trim() !== '') {
      await initInspectionFinalBase(grid);
    } else {
      console.error('Grid value is not available, invalid, or empty:', grid);
      console.error('Full inspectionDetail:', inspectionDetail);
      return;
    }
    
    changeIsClassificationGuideStartFinal(true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Box
        sx={{
          p: 4,
          width: '100%',
          maxWidth: { xs: '100%', md: '1536px' },
          height: { xs: 'auto', md: '864px' },
        }}
      >
        <Container maxWidth="xl" sx={{ height: '100%' }}>
          <Stack direction="column" justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
            <Box
              sx={{
                mb: 3,
                fontSize: { xs: '19px', sm: '48px', md: '52px' },
                fontWeight: 900,
                textAlign: 'center',
              }}
            >
              최종 분류 안내문
            </Box>

            <Container
              maxWidth="lg"
              sx={{ p: 0, height: 'calc(100% - 84px)', display: 'flex', justifyContent: 'center', mt: 5 }}
            >
              <Typography
                sx={{
                  typography: { xs: 's16w400', sm: 's18w400', md: 's20w400' },
                  display: 'block',
                  lineHeight: { xs: '28px', sm: '32px', md: '35px' },
                  wordBreak: 'keep-all',
                }}
                variant="p"
                gutterBottom
              >
                이 단계에서는 지금까지 진행한 분류를 바탕으로, 문장을 선택하는 과정입니다.
                <br />각 문장을 신중히 검토한 후, 지시에 따라 가장 적절한 문장을 선택해 주세요.
                <br />본 단계는 기존 분류를 기반으로 진행되며, 새로운 판단이 아니라 최종 선택을 조정하는 과정입니다.
                <br />
                주어진 문장의 의미를 신중히 검토하고, 객관적이고 일관된 기준을 유지해 주세요.
                <br />한 번 선택한 내용은 수정이 불가능하므로, 정확한 판단을 부탁드립니다.
              </Typography>
            </Container>
          </Stack>
        </Container>
      </Box>
      {/* 하단 버튼 영역 높이만큼 여백 추가 (버튼 높이 52px + 패딩 32px = 약 84px) */}
      <Box sx={{ height: { xs: 84, sm: 84 } }} />
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          bgcolor: 'background.paper',
          boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
          py: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <PositiveButton
          variant="contained"
          onClick={handleNext}
          sx={{ width: { xs: 130 }, height: 52, typography: 's14w500' }}
          title="시작"
        />
      </Box>
    </Box>
  );
}

export default memo(ClassificationGuideFinal);
